import { useState } from 'react';

export default function PracticalChecklist() {
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>({});

  const toggleItem = (id: string) => {
    setCheckedItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const checklistSections = [
    {
      title: 'Análisis de Ejercicios',
      items: [
        { id: 'planes', text: 'Identifico en qué plano(s) ocurre cada ejercicio' },
        { id: 'articulaciones', text: 'Reconozco qué tipo de articulación está involucrada' },
        { id: 'palanca', text: 'Determino qué tipo de palanca representa el movimiento' },
        { id: 'torque', text: 'Localizo el punto de máximo torque en el ejercicio' },
        { id: 'curva', text: 'Identifico si tiene curva ascendente, descendente o en campana' }
      ]
    },
    {
      title: 'Diseño de Programas',
      items: [
        { id: 'multiplanar', text: 'Incluyo ejercicios en los tres planos de movimiento' },
        { id: 'estabilidad', text: 'Añado trabajo de estabilización para articulaciones móviles' },
        { id: 'curvas-mix', text: 'Combino ejercicios con diferentes curvas de fuerza' },
        { id: 'progresion', text: 'Progreso acortando palanca antes de aumentar carga' },
        { id: 'individualizacion', text: 'Adapto la técnica según anatomía individual' }
      ]
    },
    {
      title: 'Ejecución Técnica',
      items: [
        { id: 'rom', text: 'Cargo solo el rango de movimiento que puedo controlar' },
        { id: 'control', text: 'Controlo la velocidad en el punto de máximo torque' },
        { id: 'momentum', text: 'Evito usar momentum o rebote en puntos críticos' },
        { id: 'angulos', text: 'Ajusto ángulos corporales para optimizar brazo de momento' },
        { id: 'estabilizacion', text: 'Activo estabilizadores antes de iniciar el movimiento' }
      ]
    },
    {
      title: 'Resolución de Problemas',
      items: [
        { id: 'dolor', text: 'Si hay dolor, analizo la biomecánica antes de descartar el ejercicio' },
        { id: 'movilidad', text: 'Trabajo movilidad sin carga antes de cargar ROM extremo' },
        { id: 'variantes', text: 'Uso variantes del ejercicio para diferentes énfasis musculares' },
        { id: 'compensaciones', text: 'Detecto y corrijo compensaciones en patrones de movimiento' },
        { id: 'feedback', text: 'Enseño a sentir qué músculos trabajan en cada variante' }
      ]
    }
  ];

  const totalItems = checklistSections.reduce((sum, section) => sum + section.items.length, 0);
  const checkedCount = Object.values(checkedItems).filter(Boolean).length;
  const progress = (checkedCount / totalItems) * 100;

  return (
    <div className="space-y-8">
      <div className="bg-white rounded-2xl p-8 border border-gray-200">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-3xl font-bold text-black mb-2">Checklist Práctico</h2>
            <p className="text-gray-600">
              Verifica que dominas estos conceptos en tu práctica diaria
            </p>
          </div>
          <div className="text-right">
            <div className="text-4xl font-bold text-[#39FF14]">{checkedCount}/{totalItems}</div>
            <p className="text-sm text-gray-500">completados</p>
          </div>
        </div>

        <div className="w-full bg-gray-200 rounded-full h-3 mb-8">
          <div
            className="bg-[#39FF14] h-3 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          ></div>
        </div>

        <div className="space-y-6">
          {checklistSections.map((section, sectionIdx) => (
            <div key={sectionIdx} className="bg-[#F5F2EB] rounded-xl p-6">
              <h3 className="text-xl font-bold text-black mb-4">{section.title}</h3>
              <div className="space-y-3">
                {section.items.map((item) => (
                  <label
                    key={item.id}
                    className="flex items-start gap-4 cursor-pointer group"
                  >
                    <div className="relative flex-shrink-0 mt-1">
                      <input
                        type="checkbox"
                        checked={checkedItems[item.id] || false}
                        onChange={() => toggleItem(item.id)}
                        className="sr-only"
                      />
                      <div
                        className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${
                          checkedItems[item.id]
                            ? 'bg-[#39FF14] border-[#39FF14]'
                            : 'bg-white border-gray-300 group-hover:border-[#39FF14]'
                        }`}
                      >
                        {checkedItems[item.id] && (
                          <i className="ri-check-line text-black text-lg"></i>
                        )}
                      </div>
                    </div>
                    <span
                      className={`text-base leading-relaxed transition-all ${
                        checkedItems[item.id]
                          ? 'text-gray-500 line-through'
                          : 'text-gray-700 group-hover:text-black'
                      }`}
                    >
                      {item.text}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {progress === 100 && (
        <div className="bg-gradient-to-r from-[#39FF14] to-[#2EE000] rounded-2xl p-8 text-center">
          <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-trophy-line text-4xl text-[#39FF14]"></i>
          </div>
          <h3 className="text-2xl font-bold text-black mb-2">¡Checklist Completo!</h3>
          <p className="text-black/80">
            Has verificado todos los conceptos clave. Ahora aplícalos consistentemente en tu entrenamiento.
          </p>
        </div>
      )}
    </div>
  );
}